import React, { useState, useEffect, useMemo } from 'react';
import { Container, Row, Col, Card, Badge, Table, Form, Button, Spinner, Alert, Modal } from 'react-bootstrap';
import { FaCheckCircle, FaTimesCircle, FaClock, FaPercentage, FaHistory, FaWallet, FaDownload, FaGift, FaCalendar } from 'react-icons/fa';
import SendToClientService from '../services/SendToClient_service';
import { getMyDiscountHistory } from '../services/Solde_service';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { toast } from 'react-toastify';
import '../css/MyBalance.css';
import axios from 'axios';
import { Tooltip } from 'antd';

interface DiscountCode {
  id: number;
  code: string;
  amount: number;
  discountPercentage: number;
  clientEmail: string;
  isUsed: boolean;
  usedAt?: string;
  expiresAt: string;
  createdAt: string;
}

interface DiscountClaim {
  id: number;
  totalAmount: number;
  percentageDiscount: number;
  pointsDiscount: number;
  creditNoteDiscount: number;
  usedPoints: number;
  usedAvoirsIds: number[];
  createdAt: string;
}

const MyBalance: React.FC = () => {
  const user = useSelector((state: RootState) => state.user.user);
  const soldeRemise = user?.soldeRemise || 0;
  const [myCodes, setMyCodes] = useState<DiscountCode[]>([]);
  const [loading, setLoading] = useState(false);
  const [codeInput, setCodeInput] = useState('');
  const [validating, setValidating] = useState(false);
  const [showUseModal, setShowUseModal] = useState(false);
  const [selectedCode, setSelectedCode] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  // État pour l'historique des remises
  const [discountHistory, setDiscountHistory] = useState<DiscountClaim[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const [withdrawAmount, setWithdrawAmount] = useState<number>(0);
  const [withdrawRequests, setWithdrawRequests] = useState<any[]>([]);
  const [loadingWithdraw, setLoadingWithdraw] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [iban, setIban] = useState('');

  const [rib, setRib] = useState(user?.account_number || '');
  const [bic, setBic] = useState(user?.bic_swift || '');

  const [showRecap, setShowRecap] = useState(false);

  const [withdrawFilterStatus, setWithdrawFilterStatus] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all');

  const filteredWithdrawRequests = useMemo(() => {
    if (withdrawFilterStatus === 'all') return withdrawRequests;
    return withdrawRequests.filter(w => w.status === withdrawFilterStatus);
  }, [withdrawRequests, withdrawFilterStatus]);

  const handleDownload = (req: any) => {
    window.open(`${process.env.REACT_APP_API_URL}/solde/${req.id}/download-invoice`, "_blank");
  };

  useEffect(() => {
    if (user) {
      setRib(user.account_number || '');
      setBic(user.bic_swift || '');
    }
  }, [user]);

  const loadMyCodes = async () => {
    setLoading(true);
    setError(null);
    try {
      const codes = await SendToClientService.getMyDiscountCodes();
      setMyCodes(Array.isArray(codes) ? codes : []);
    } catch (err: any) {
      console.error('Erreur:', err);
      setError('Impossible de charger vos codes de remise');
      toast.error('❌ Erreur lors du chargement des codes');
      setMyCodes([]);
    } finally {
      setLoading(false);
    }
  };

  const loadWithdrawRequests = async () => {
    if (!user?.id) return;

    setLoadingWithdraw(true);
    try {
      const token = localStorage.getItem("token");

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/solde/users/withdraw-requests`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setWithdrawRequests(response.data.data);
      } else {
        toast.error("Erreur lors du chargement des demandes de retrait");
      }
    } catch (err) {
      console.error("loadWithdrawRequests error:", err);
      toast.error("Erreur lors du chargement des demandes de retrait");
    } finally {
      setLoadingWithdraw(false);
    }
  };

  const loadDiscountHistory = async () => {
    setLoadingHistory(true);
    try {
      const result = await getMyDiscountHistory();
      if (result.success) {
        setDiscountHistory(result.data);
      } else {
        toast.error("Erreur lors du chargement de l'historique");
      }
    } catch (err) {
      console.error("Erreur loadDiscountHistory:", err);
      toast.error("Erreur lors du chargement de l'historique");
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    loadMyCodes();
    loadWithdrawRequests();
    loadDiscountHistory();
  }, []);


  const handleRequestWithdraw = async () => {
    if (withdrawAmount <= 0) {
      toast.warning("⚠️ Montant invalide");
      return;
    }
    if (withdrawAmount > soldeRemise) {
      toast.warning("⚠️ Montant supérieur au solde disponible");
      return;
    }
    if (!rib.trim() || !bic.trim()) {
      toast.warning("⚠️ Veuillez renseigner toutes les informations bancaires");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/solde/withdraw/request`,
        { amount: withdrawAmount, rib, iban, bic },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success("✅ Demande de retrait envoyée !");
        setWithdrawAmount(0);
        setRib('');
        setIban('');
        setBic('');
        setShowWithdrawModal(false);
        loadWithdrawRequests();
      } else {
        toast.error(response.data.message || "Erreur lors de la demande de retrait");
      }
    } catch (err: any) {
      console.error("handleRequestWithdraw error:", err);
      toast.error(err.response?.data?.message || "Erreur serveur");
    }
  };

  const handleUseCode = async () => {
    if (!selectedCode) return;

    setValidating(true);
    try {
      const result = await SendToClientService.applyDiscountCode(selectedCode.code);

      if (result.success) {
        toast.success(`🎉 Remise de ${selectedCode.discountPercentage}€ appliquée avec succès!`, {
          position: 'top-center',
          autoClose: 3000
        });
        setCodeInput('');
        setShowUseModal(false);
        setSelectedCode(null);
        loadMyCodes();
      } else {
        toast.error(`❌ ${result.message || 'Erreur lors de l\'activation'}`);
      }
    } catch (err: any) {
      console.error('Erreur:', err);
      const errorMsg = err.response?.data?.message || err.message || 'Erreur lors de l\'utilisation du code';
      toast.error(`❌ ${errorMsg}`);
    } finally {
      setValidating(false);
    }
  };

  // Calculs des totaux
  const totalPending = useMemo(
    () => withdrawRequests
      .filter(w => w.status === 'pending')
      .reduce((sum, w) => sum + Number(w.amount), 0),
    [withdrawRequests]
  );

  const totalApproved = useMemo(
    () => withdrawRequests
      .filter(w => w.status === 'approved')
      .reduce((sum, w) => sum + Number(w.amount), 0),
    [withdrawRequests]
  );

  const totalRejected = useMemo(
    () => withdrawRequests
      .filter(w => w.status === 'rejected')
      .reduce((sum, w) => sum + Number(w.amount), 0),
    [withdrawRequests]
  );


  return (
    <Container fluid className="my-balance-page py-4" style={{ maxWidth: '100%', paddingLeft: '20px', paddingRight: '20px' }}>
      <Row className="mb-5">
        <Col md={12}>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h1 className="fw-bold mb-1" style={{ fontSize: '2rem', color: '#2A919D', letterSpacing: '-0.5px' }}>Mon Portefeuille</h1>
              <p className="text-muted mb-0">Gérez vos codes de remise et votre solde</p>
            </div>
            <div className="d-flex gap-3">
              <Button
                style={{ background: 'linear-gradient(135deg, #4BB0C0 0%, #2A919D 100%)', border: 'none' }}
                className="d-flex align-items-center"
                onClick={loadMyCodes}
                disabled={loading}
              >
                {loading ? <Spinner size="sm" /> : 'Actualiser'}
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      {/* Solde et Validation */}
      <Row className="mb-5">
        <Col md={12}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Body className="p-4">
              <div className="d-flex align-items-center mb-4">
                <div className="rounded-circle d-flex align-items-center justify-content-center me-3"
                  style={{ width: '48px', height: '48px', background: 'linear-gradient(135deg, #4BB0C0 0%, #2A919D 100%)' }}>
                  <FaWallet style={{ fontSize: '24px', color: 'white' }} />
                </div>
                <div>
                  <h6 className="text-muted mb-1">Solde disponible</h6>
                  <h2 className="fw-bold mb-0" style={{ color: '#2A919D', fontSize: '2.5rem' }}>
                    {parseFloat(String(soldeRemise)).toFixed(2)} €
                  </h2>
                </div>
              </div>

              <div className="d-flex justify-content-between align-items-center">
                <Tooltip
                  title="Aucun remboursement ne peut être effectué pour un montant inférieur à 100 €"
                  placement="top"
                  open={soldeRemise < 100 ? undefined : false}
                >
                  <span>
                    <Button
                      onClick={() => setShowWithdrawModal(true)}
                      disabled={soldeRemise < 100}
                      className="px-4"
                      style={{
                        background:
                          soldeRemise >= 100
                            ? 'linear-gradient(135deg, #F4A261 0%, #F4A261 100%)'
                            : '#e9ecef',
                        border: 'none',
                        color: soldeRemise >= 100 ? '#fff' : '#000',
                        fontWeight: 600,
                        cursor: soldeRemise < 100 ? 'not-allowed' : 'pointer'
                      }}
                    >
                      Demander un retrait
                    </Button>
                  </span>
                </Tooltip>

                <small className="text-muted">
                  {soldeRemise < 100 ? 'Minimum 100€ pour retirer' : 'Retrait disponible'}
                </small>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mb-5">
        {/* Retraits en attente */}
        <Col md={4} className="mb-3">
          <Card className="border-0 shadow-sm h-100 hover-lift">
            <Card.Body className="p-4">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-muted mb-2">En attente</h6>
                  <h3 className="fw-bold mb-0" style={{ color: '#4BB0C0' }}>
                    {totalPending.toFixed(2)} €
                  </h3>
                </div>
                <div className="rounded-circle d-flex align-items-center justify-content-center"
                  style={{ width: '40px', height: '40px', backgroundColor: 'rgba(42, 145, 157, 0.1)' }}>
                  <FaClock style={{ color: '#4BB0C0' }} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Retraits approuvés */}
        <Col md={4} className="mb-3">
          <Card className="border-0 shadow-sm h-100 hover-lift">
            <Card.Body className="p-4">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-muted mb-2">Approuvés</h6>
                  <h3 className="fw-bold mb-0" style={{ color: '#2A919D' }}>
                    {totalApproved.toFixed(2)} €
                  </h3>
                </div>
                <div className="rounded-circle d-flex align-items-center justify-content-center"
                  style={{ width: '40px', height: '40px', backgroundColor: 'rgba(42, 145, 157, 0.1)' }}>
                  <FaCheckCircle style={{ color: '#2A919D' }} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Retraits rejetés */}
        <Col md={4} className="mb-3">
          <Card className="border-0 shadow-sm h-100 hover-lift">
            <Card.Body className="p-4">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-muted mb-2">Rejetés</h6>
                  <h3 className="fw-bold mb-0" style={{ color: '#F4A261' }}>
                    {totalRejected.toFixed(2)} €
                  </h3>
                </div>
                <div className="rounded-circle d-flex align-items-center justify-content-center"
                  style={{ width: '40px', height: '40px', backgroundColor: 'rgba(220, 53, 69, 0.1)' }}>
                  <FaTimesCircle style={{ color: '#F4A261' }} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>


      {/* Section Récapitulatif des Remises - Mois en cours */}
      {showRecap && (
        <Row className="mb-5">
          <Col md={12}>
            <Card className="border-0 shadow-sm">
              <Card.Header className="bg-white border-0 py-3">
                <div className="d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center">
                    <div className="rounded-circle d-flex align-items-center justify-content-center me-3"
                      style={{ width: '40px', height: '40px', backgroundColor: 'rgba(42, 145, 157, 0.1)' }}>
                      <FaPercentage style={{ color: '#2A919D' }} />
                    </div>
                    <div>
                      <h5 className="mb-0 fw-bold" style={{ color: '#2A919D' }}>
                        Récapitulatif des Remises - {new Date().toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
                      </h5>
                      <small className="text-muted">
                        Vue d'ensemble par type de remise pour le mois en cours
                      </small>
                    </div>
                  </div>

                  {/* Indicateur du mois */}
                  <Badge bg="light" text="dark" className="px-3 py-2">
                    <FaCalendar className="me-2" />
                    {new Date().toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
                  </Badge>
                </div>
              </Card.Header>

              <Card.Body className="p-0">
                <div className="table-responsive">
                  <Table className="mb-0" hover>
                    <thead className="table-light">
                      <tr>
                        <th style={{ color: '#2A919D', fontWeight: 'bold', minWidth: '150px' }}>
                          Type de remise
                        </th>
                        <th style={{ color: '#2A919D', fontWeight: 'bold', textAlign: 'center' }}>
                          Total du mois
                        </th>
                        <th style={{ color: '#2A919D', fontWeight: 'bold', textAlign: 'center' }}>
                          Total cumulé
                        </th>
                        <th style={{ color: '#2A919D', fontWeight: 'bold', textAlign: 'center' }}>
                          Solde disponible en €
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const currentMonth = new Date().getMonth();
                        const currentYear = new Date().getFullYear();

                        // Variables pour les QUANTITÉS
                        let monthlyPercentCount = 0;
                        let monthlyTotalPoints = 0;
                        let monthlyTotalAvoirs = 0;

                        let totalPercentCount = 0;
                        let totalCumulatedPoints = 0;
                        let totalCumulatedAvoirs = 0;

                        // Variables pour les VALEURS en €
                        let monthlyPercentValue = 0;
                        let monthlyPointsValue = 0;
                        let monthlyAvoirsValue = 0;

                        let totalPercentValue = 0;
                        let totalPointsValue = 0;
                        let totalAvoirsValue = 0;

                        discountHistory.forEach(claim => {
                          const claimDate = new Date(claim.createdAt);
                          const claimMonth = claimDate.getMonth();
                          const claimYear = claimDate.getFullYear();

                          const percentage = Number(claim.percentageDiscount) || 0;
                          const pointsValue = Number(claim.pointsDiscount) || 0;
                          const avoirsValue = Number(claim.creditNoteDiscount) || 0;
                          const usedPoints = Number(claim.usedPoints) || 0;
                          const avoirsCount = claim.usedAvoirsIds?.length || 0;

                          // Calculs cumulés (tous temps)
                          if (percentage > 0) totalPercentCount++;
                          totalCumulatedPoints += usedPoints;
                          totalCumulatedAvoirs += avoirsCount;

                          totalPercentValue += percentage;
                          totalPointsValue += pointsValue;
                          totalAvoirsValue += avoirsValue;

                          // Calculs du mois en cours
                          if (claimMonth === currentMonth && claimYear === currentYear) {
                            if (percentage > 0) monthlyPercentCount++;
                            monthlyTotalPoints += usedPoints;
                            monthlyTotalAvoirs += avoirsCount;

                            monthlyPercentValue += percentage;
                            monthlyPointsValue += pointsValue;
                            monthlyAvoirsValue += avoirsValue;
                          }
                        });

                        return (
                          <>
                            {/* Ligne Remise % */}
                            <tr>
                              <td>
                                <div className="d-flex align-items-center">
                                  <div
                                    className="me-2 rounded d-flex align-items-center justify-content-center"
                                    style={{
                                      backgroundColor: '#2A919D',
                                      color: 'white',
                                      width: '32px',
                                      height: '32px',
                                      fontSize: '1rem',
                                      fontWeight: 'bold'
                                    }}
                                  >
                                    %
                                  </div>
                                  <div>
                                    <strong>Remise %</strong>
                                    <div className="text-muted" style={{ fontSize: '0.85rem' }}>
                                      Code Promotionnel ou Commission
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                <div>{monthlyPercentValue.toFixed(2)} €</div>
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                <div>{totalPercentValue.toFixed(2)} €</div>
                              </td>
                              <td style={{ textAlign: 'center', fontSize: '1.1rem', fontWeight: '600', color: '#f4a261' }}>
                                {totalPercentValue.toFixed(2)} €
                              </td>
                            </tr>

                            {/* Ligne Points */}
                            <tr>
                              <td>
                                <div className="d-flex align-items-center">
                                  <div
                                    className="me-2 rounded d-flex align-items-center justify-content-center"
                                    style={{
                                      backgroundColor: '#2A919D',
                                      color: 'white',
                                      width: '32px',
                                      height: '32px',
                                      fontSize: '1rem',
                                      fontWeight: 'bold'
                                    }}
                                  >
                                    <FaGift size={14} />
                                  </div>

                                  <div>
                                    <strong>Points</strong>
                                    <div className="text-muted" style={{ fontSize: '0.85rem' }}>
                                      Programme de fidélité
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                <div>{monthlyTotalPoints.toLocaleString()} pts</div>
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                <div>{totalCumulatedPoints.toLocaleString()} pts</div>
                              </td>
                              <td style={{ textAlign: 'center', fontSize: '1.1rem', fontWeight: '600', color: '#f4a261' }}>
                                {totalPointsValue.toFixed(2)} €
                              </td>
                            </tr>

                            {/* Ligne Avoir */}
                            <tr>
                              <td>
                                <div className="d-flex align-items-center">
                                  <div
                                    className="me-2 rounded d-flex align-items-center justify-content-center"
                                    style={{
                                      backgroundColor: '#2A919D',
                                      color: 'white',
                                      width: '32px',
                                      height: '32px',
                                      fontSize: '1rem',
                                      fontWeight: 'bold'
                                    }}
                                  >
                                    <FaWallet size={14} />
                                  </div>
                                  <div>
                                    <strong>Avoir</strong>
                                    <div className="text-muted" style={{ fontSize: '0.85rem' }}>
                                      Crédit disponible
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                <div>{monthlyAvoirsValue.toFixed(2)} €</div>
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                <div>{totalAvoirsValue.toFixed(2)} €</div>
                              </td>
                              <td style={{ textAlign: 'center', fontSize: '1.1rem', fontWeight: '600', color: '#f4a261' }}>
                                {totalAvoirsValue.toFixed(2)} €
                              </td>
                            </tr>
                          </>
                        );
                      })()}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Historique des réclamations de remises */}
      <Row className="mb-5 mt-5">
        <Col md={12}>
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-white border-0 py-3 d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center">
                <FaHistory style={{ fontSize: '20px', color: '#2A919D', marginRight: '10px' }} />
                <h5 className="mb-0 fw-bold" style={{ color: '#2A919D' }}>Historique de mes remises réclamées</h5>
              </div>
              <div className="d-flex gap-2">
                {/* Bouton pour afficher/masquer le récapitulatif */}
                <Button
                  size="sm"
                  style={{
                    backgroundColor: showRecap ? "#4bb0c0" : "#f4a261",
                    borderColor: showRecap ? "#4bb0c0" : "#f4a261",
                    padding: "6px 12px",
                    fontWeight: 600,
                  }}
                  onClick={() => setShowRecap(!showRecap)}
                  className="d-flex align-items-center"

                >
                  {showRecap ? "Masquer récapitulatif" : "Voir récapitulatif"}
                </Button>

                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={loadDiscountHistory}
                  disabled={loadingHistory}
                >
                  {loadingHistory ? <Spinner size="sm" /> : 'Actualiser'}
                </Button>
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              {loadingHistory ? (
                <div className="text-center py-5">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Chargement...</span>
                  </Spinner>
                </div>
              ) : discountHistory.length === 0 ? (
                <div className="text-center py-5">
                  <p className="text-muted">Aucune remise réclamée pour le moment</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <Table className="mb-0" hover>
                    <thead className="table-light">
                      <tr>
                        <th style={{ color: '#2A919D', fontWeight: 'bold' }}>Date</th>
                        <th style={{ color: '#2A919D', fontWeight: 'bold' }}>Remise %</th>
                        <th style={{ color: '#2A919D', fontWeight: 'bold' }}>Remise Points</th>
                        <th style={{ color: '#2A919D', fontWeight: 'bold' }}>Remise Avoir</th>
                        <th style={{ color: '#2A919D', fontWeight: 'bold', textAlign: 'right' }}>Total (€)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {discountHistory.map((claim) => {
                        const percentageDiscount = Number(claim.percentageDiscount) || 0;
                        const pointsDiscount = Number(claim.pointsDiscount) || 0;
                        const creditNoteDiscount = Number(claim.creditNoteDiscount) || 0;
                        const totalAmount = Number(claim.totalAmount) || 0;
                        const usedPoints = Number(claim.usedPoints) || 0;

                        return (
                          <tr key={claim.id}>
                            <td>
                              <small className="text-muted">
                                {new Date(claim.createdAt).toLocaleDateString('fr-FR', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric'
                                })}
                                <br />
                                <strong style={{ fontSize: '0.85rem' }}>
                                  {new Date(claim.createdAt).toLocaleTimeString('fr-FR', {
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </strong>
                              </small>
                            </td>
                            <td>
                              {percentageDiscount > 0 ? (
                                <div className="d-flex align-items-center" style={{ width: 'fit-content', backgroundColor: '#f4a261', padding: '5px 10px', borderRadius: '15px', color: 'white' }}>
                                  <FaPercentage style={{ marginRight: '5px' }} />
                                  {percentageDiscount.toFixed(2)}€
                                </div>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            <td>
                              {pointsDiscount > 0 ? (
                                <div className="d-flex align-items-center" style={{ width: 'fit-content', backgroundColor: '#4BB0C0', padding: '5px 10px', borderRadius: '15px', color: 'white' }}>
                                  <FaGift style={{ marginRight: '5px' }} />
                                  {usedPoints} pts / {pointsDiscount.toFixed(2)}€
                                </div>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            <td>
                              {creditNoteDiscount > 0 ? (
                                <div className="d-flex align-items-center" style={{ width: 'fit-content', backgroundColor: '#f7931e', padding: '5px 10px', borderRadius: '15px', color: 'white' }}>
                                  <FaCheckCircle style={{ marginRight: '5px' }} />
                                  {creditNoteDiscount.toFixed(2)}€
                                </div>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            <td style={{ textAlign: 'right' }}>
                              <strong style={{ color: '#2A919D', fontSize: '1.2rem' }}>
                                {totalAmount.toFixed(2)}€
                              </strong>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <Card className="border-0 shadow-sm mt-4">
        <Card.Header className="bg-white border-0 py-3 d-flex justify-content-between align-items-center">
          <h5 className="mb-0 fw-bold" style={{ color: '#2A919D' }}>Mes demandes de retrait</h5>
          <Form.Select
            size="sm"
            style={{ maxWidth: '180px' }}
            value={withdrawFilterStatus}
            onChange={(e) => setWithdrawFilterStatus(e.target.value as 'all' | 'pending' | 'approved' | 'rejected')}
          >
            <option value="all">Tous</option>
            <option value="pending">En attente</option>
            <option value="approved">Approuvé</option>
            <option value="rejected">Rejeté</option>
          </Form.Select>

        </Card.Header>
        <Card.Body className="p-0">
          {loadingWithdraw ? (
            <div className="text-center py-3">
              <Spinner animation="border" />
            </div>
          ) : filteredWithdrawRequests.length === 0 ? (
            <div className="text-center py-3 text-muted">Aucune demande de retrait</div>
          ) : (
            <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
              <Table hover className="mb-0 text-center">
                <thead>
                  <tr>
                    <th>Montant</th>
                    <th>Statut</th>
                    <th>Date d'envoie</th>
                    <th>Facture</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredWithdrawRequests.map((req) => (
                    <tr key={req.id}>
                      <td>{Number(req.amount).toFixed(2)} €</td>
                      <td>
                        {req.status === 'pending' && <Badge bg="warning">En attente</Badge>}
                        {req.status === 'approved' && <Badge bg="success">Approuvé</Badge>}
                        {req.status === 'rejected' && <Badge bg="danger">Rejeté</Badge>}
                      </td>
                      <td>{new Date(req.createdAt).toLocaleDateString()}</td>
                      <td>
                        {req.status === "approved" && (
                          <Button
                            size="sm"
                            variant="primary"
                            onClick={() => handleDownload(req)}
                          >
                            <FaDownload className="me-1" />
                            Télécharger
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>



      <Modal
        show={showWithdrawModal}
        onHide={() => setShowWithdrawModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Demande de retrait</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Votre solde disponible : <strong>{parseFloat(String(soldeRemise)).toFixed(2)} €</strong></p>

          <Form.Group className="mb-3">
            <Form.Label>Montant à retirer</Form.Label>
            <Form.Control
              type="number"
              min={1}
              max={soldeRemise}
              value={withdrawAmount}
              onChange={(e) => setWithdrawAmount(Number(e.target.value))}
            />
            <Form.Text className="text-muted">
              Minimum 100€ pour effectuer un retrait.
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>RIB (23 caractères)</Form.Label>
            <Form.Control
              type="text"
              value={rib}
              maxLength={23}
              onChange={(e) => setRib(e.target.value)}
              placeholder="Entrez votre RIB"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>BIC/SWIFT (8 ou 11 caractères)</Form.Label>
            <Form.Control
              type="text"
              value={bic}
              maxLength={11}
              onChange={(e) => setBic(e.target.value)}
              placeholder="Entrez votre BIC"
            />

          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowWithdrawModal(false)}>
            Annuler
          </Button>
          <Button
            variant="primary"
            disabled={
              withdrawAmount < 100 ||
              withdrawAmount > soldeRemise ||
              rib.trim().length !== 23 ||
              bic.trim().length < 8 || bic.trim().length > 11
            }

            onClick={handleRequestWithdraw}
          >
            {withdrawAmount >= 100 ? 'Envoyer la demande' : 'Montant insuffisant'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal: Confirmation d'utilisation */}
      <Modal show={showUseModal} onHide={() => setShowUseModal(false)} centered>
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold" style={{ color: '#2A919D' }}>Activer le code</Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-4">
          {selectedCode && (
            <div className="text-center">
              <div className="mb-4">
                <div className="rounded-circle d-inline-flex align-items-center justify-content-center p-3 mb-3"
                  style={{ backgroundColor: 'rgba(75, 176, 192, 0.1)' }}>
                  <FaGift size={32} style={{ color: '#4BB0C0' }} />
                </div>

                <h5 className="mb-2">Code validé avec succès</h5>

                <div className="mb-4">
                  <code className="px-4 py-2 rounded d-inline-block mb-3"
                    style={{
                      backgroundColor: '#f8f9fa',
                      fontSize: '1.1rem',
                      fontWeight: '600',
                      color: '#2A919D',
                      letterSpacing: '1px'
                    }}>
                    {selectedCode.code}
                  </code>
                  <div className="display-4 fw-bold mb-3" style={{ color: '#FF8C00' }}>
                    {parseFloat(String(selectedCode.discountPercentage)).toFixed(2)} €
                  </div>
                </div>

                <Alert variant="light" className="border-start border-primary border-3">
                  <div className="d-flex align-items-start">
                    <FaCheckCircle className="me-2 mt-1" style={{ color: '#28a745' }} />
                    <span>{selectedCode.message}</span>
                  </div>
                </Alert>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0">
          <Button
            variant="light"
            onClick={() => setShowUseModal(false)}
            disabled={validating}
            className="px-4"
            style={{ color: '#6c757d' }}
          >
            Annuler
          </Button>
          <Button
            onClick={handleUseCode}
            disabled={validating}
            className="px-4"
            style={{
              background: 'linear-gradient(135deg, #4BB0C0 0%, #2A919D 100%)',
              border: 'none'
            }}
          >
            {validating ? (
              <>
                <Spinner size="sm" animation="border" className="me-2" />
                Activation en cours...
              </>
            ) : (
              <>
                <FaCheckCircle className="me-2" />
                Confirmer l'activation
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default MyBalance;